import * as Sentry from '@sentry/vue'

/**
 * Report an error to Sentry
 *
 * @param {Error} error - The error object to report
 * @param {object} context - Additional context to attach to the error
 */
export const reportError = (error, context = {}) => {
  // Always log to console
  console.error('[Error]', error, context)

  // Report to Sentry if initialized
  Sentry.captureException(error, {
    extra: context,
    tags: {
      component: context.component,
      action: context.action
    }
  })
}

/**
 * Report a custom message to Sentry
 *
 * @param {string} message - The message to report
 * @param {string} severity - The severity level (error, warning, info, debug)
 * @param {object} context - Additional context to attach to the message
 */
export const reportMessage = (message, severity = 'error', context = {}) => {
  // Log to console
  const logFn = console[severity] || console.log
  logFn('[Message]', message, context)

  // Map severity to Sentry level
  const sentryLevel =
    severity === 'error'
      ? 'error'
      : severity === 'warning'
        ? 'warning'
        : severity === 'info'
          ? 'info'
          : 'debug'

  // Report to Sentry
  Sentry.captureMessage(message, {
    level: sentryLevel,
    extra: context
  })
}

/**
 * Set user context for error reporting
 *
 * @param {object} user - User object with id, email, etc.
 */
export const setUserContext = (user) => {
  if (user) {
    Sentry.setUser({
      id: user.uid || user.id,
      email: user.email,
      username: user.displayName || user.name
    })
  } else {
    Sentry.setUser(null)
  }
}

/**
 * Get current user context (deprecated - use Sentry's built-in user tracking)
 * Kept for backward compatibility
 */
export const getUserContext = () => {
  // This is now handled by Sentry internally
  return null
}
